
import { Component } from 'vue-property-decorator';
import WizardContentView
  from '@/ui/components/wizards/baseComponents/WizardContentView.vue';
import { mixins } from 'vue-class-component';
import { Validation } from '@/ui/mixins/validation';
import { Action, Getter, Mutation, State } from 'vuex-class';
import { ITenantWizardState } from '@/store/modules/tenantWizard/types';
import { cloneDeep } from 'lodash';
import InfoTooltip from '@/ui/components/components/InfoTooltip.vue';
import { initFlatsAddresses, hasArrayDuplicateValue, filterNumberFromString } from '@/utils/tenantWizardUtilsFunctions';
import { counterLimits } from '@/ui/components/wizards/tenantWizard/wizardSettings/limits';
import { IFlatsConfig, ITenantModel } from '@/types/wizards/tenant.types';
import { IProject } from '@/types/project.types';

@Component({
  components: {
    WizardContentView,
    InfoTooltip,
  },
})
export default class Cooling extends mixins(Validation) {
  @State('tenantWizard') tenantState!: ITenantWizardState;
  @Getter('tenantWizard/isAutoMappingEnabled') isAutoMappingEnabled!: boolean;
  @Getter('variables/variablesForComboBox') noAutomappingVaribles!: [];
  @Getter('projects/project') project!: IProject;
  @Mutation('tenantWizard/updateTenantObject') updateTenantObject!: (data: ITenantModel) => void;
  @Action('variables/fetchVariables') fetchVariables!: (projectId: string) => Promise<void>;
  @Action('tenantWizard/handleIncrement') handleIncrement!: () => void;
  @Action('tenantWizard/handleDecrement') handleDecrement!: () => void;

  localCounterLimits = counterLimits;
  valid = false;
  sideCosts = 0;
  flatsConfigList: IFlatsConfig[] = [];
  flatsFullAddress: Record<string, string> = {};
  overallCoolingCounterIdArray: any = [];

  overallCoolingCounterNumberList: any[] = [];

  valueNotAllowedCheck(value: any) {
    if (!this.isAutoMappingEnabled) {
      return true;
    }
    const number = parseInt(value, 10);
    if (!this.variablesForComboBox.includes(number)) {
      return this.$t('tenantWizard.valueNotAllowed');
    }
    return true;
  }

  get counterAmount() {
    let otherCounterNumber = 0;
    let flatCounterNumber = 0;

    if (this.isAutoMappingEnabled) {
      otherCounterNumber = this.overallCoolingCounterNumberList.length + 2;
    } else {
      otherCounterNumber = this.overallCoolingCounterIdArray.length + 2;
    }

    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flatCounterNumber += flat.counters.coolingCounter.length;
    });
    return otherCounterNumber + flatCounterNumber;
  }

  get variablesForComboBox() {
    // if automapping is disabled we just return normal meassurement variables
    if (!this.isAutoMappingEnabled) {
      return this.noAutomappingVaribles;
    }
    // if automapping is enabled we return all numbers from 1 to 255
    return Array.from({ length: 255 }, (_, index) => index + 1);
  }

  get hasDuplicateValue(): boolean | string {
    // will contain all numbers that are set either inside overall counters or inside flats counters
    let array = [];
    // if automapping is disabled we disable this validation
    if (!this.isAutoMappingEnabled) {
      array = [this.overallCoolingCounterIdArray.map((value: { id: string; name: string}) => value.id)].flat();
    } else {
      array = [this.overallCoolingCounterNumberList.map((value: { id: string; name: string}) => value.id)].flat();
    }

    // add flats counters
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.coolingCounter.forEach((counter: any) => {
        array.push(counter.id);
      });
    });

    return hasArrayDuplicateValue(array, 'tenantWizard.hasDuplicateValue');
  }

  get hasDuplicateName(): boolean | string {
    // will contain all numbers that are set either inside overall counters or inside flats counters
    let array = [];

    if (this.isAutoMappingEnabled) {
      array = [this.overallCoolingCounterNumberList.map((value: { id: string; name: string}) => value.name)].flat();
    } else {
      array = [this.overallCoolingCounterIdArray.map((value: { id: string; name: string}) => value.name)].flat();
    }

    // add flats counters
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.coolingCounter.forEach((counter: any) => {
        array.push(counter.name);
      });
    });

    return hasArrayDuplicateValue(array, 'tenantWizard.hasDuplicateName') ?? true;
  }

  created() {
    this.fetchVariables(this.$route.params.id);
    this.flatsConfigList = this.tenantState.tenant.general.flats.flatConfigurations;
    this.flatsConfigList.forEach((flat: IFlatsConfig, index: number) => {
      if (this.flatsConfigList[index].counters?.coolingCounter === null) {
        this.flatsConfigList[index].counters.coolingCounter = [];
      }
    });
    if (this.flatsConfigList[0].counters.coolingCounter.length === 0) {
      this.flatsConfigList.forEach((flat: IFlatsConfig) => {
        flat.counters.coolingCounter.push({ id: '', name: '' });
      });
    }
    if (this.tenantState.tenant.general.overallCounters.coolingCounter !== null) {
      this.overallCoolingCounterIdArray = this.tenantState.tenant.general.overallCounters.coolingCounter;
    }
    if (this.isAutoMappingEnabled) {
      this.fillNumbers();
    }
  }

  fillNumbers() {
    // overall part
    this.overallCoolingCounterNumberList = this.overallCoolingCounterIdArray.map((counter: any) => {
      return { id: filterNumberFromString(counter.id), name: counter.name };
    });
    // flats part
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.coolingCounter.forEach((counter: any) => {
        counter.id = filterNumberFromString(counter.id);
      });
    });
  }

  removeOverallItem(index: number) {
    if (this.isAutoMappingEnabled) {
      this.overallCoolingCounterNumberList.splice(index, 1);
    } else {
      this.overallCoolingCounterIdArray.splice(index, 1);
    }
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  addOverallItem() {
    if (this.isAutoMappingEnabled) {
      this.overallCoolingCounterNumberList.push({ id: '', name: '' });
    } else {
      this.overallCoolingCounterIdArray.push({ id: '', name: '' });
    }
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  removeCounterFromFlat(flatIndex: number, counterIndex: number) {
    this.flatsConfigList[flatIndex].counters.coolingCounter.splice(counterIndex, 1);
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  addCounterToFlat(flatIndex: number) {
    this.flatsConfigList[flatIndex].counters.coolingCounter.push({ id: '', name: '' });
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  mounted() {
    this.flatsFullAddress = initFlatsAddresses(this.flatsConfigList);
    this.$nextTick(() => {
      (this.$refs.form as any).validate();
    });
  }

  handleBack() {
    // if we are going back and we are in the automapping mode we need to convert the numbers to the variable names
    if (this.isAutoMappingEnabled) {
      this.convertNumbersToVariables();
    }
    this.handleDecrement();
  }

  handleNext() {
    if (this.isAutoMappingEnabled) {
      this.convertNumbersToVariables();
    }
    this.updateTenant();
    this.handleIncrement();
  }

  convertNumbersToVariables() {
    // overall part
    this.overallCoolingCounterIdArray = this.overallCoolingCounterNumberList.map((counter: any) => {
      return { id: `prgHM.lrCounter_C_C_${counter.id}`, name: counter.name };
    });
    // flats part
    this.flatsConfigList.forEach((flat: IFlatsConfig) => {
      flat.counters.coolingCounter.forEach((counter: any) => {
        counter.id = `prgHM.lrCounter_C_C_${counter.id}`;
      });
    });
  }

  updateTenant() {
    const tenantCopy = cloneDeep(this.tenantState.tenant);
    tenantCopy.general.flats.flatConfigurations = this.flatsConfigList;
    tenantCopy.general.overallCounters.coolingCounter = this.overallCoolingCounterIdArray;
    this.updateTenantObject(tenantCopy);
  }
}
